import cx from 'classnames'
import { useContext, useRef, useMemo, useEffect } from 'react'
import { useMediaQuery, useResizeObserver } from 'usehooks-ts'

import { type Step } from '../lib/step-definition'
import { FrameBuilderDefaultFrameContext } from '../contexts/default-frame-context'
import { FrameBuilderSelectedStateContext } from '../contexts/state-context'

import Builder from './builder'
import FrameViewer from './frame-viewer'
import Gallery from './gallery'

interface FrameBuilderProps {
  showExtrasStep: boolean
  showProgressBar: boolean
  onOpenStep?: (step: Step) => void
  onAddToCart?: () => void
}

const FrameBuilder = ({
  showExtrasStep,
  showProgressBar,
  onOpenStep,
  onAddToCart,
}: FrameBuilderProps) => {
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)
  const { isDefaultStateLoaded } = useContext(FrameBuilderDefaultFrameContext)

  const frameViewerRef = useRef<HTMLCanvasElement | null>(null)

  const isScreenSmallOrLarger = useMediaQuery(`(min-width: 768px)`)

  // Calculated widths and heights
  const layoutRef = useRef<HTMLDivElement | null>(null)
  const builderRef = useRef<HTMLDivElement | null>(null)
  const viewerWrapperRef = useRef<HTMLDivElement | null>(null)
  const layoutRectangle = useResizeObserver({
    ref: layoutRef,
  })
  const builderRectangle = useResizeObserver({
    ref: builderRef,
  })
  const viewerWrapperRectangle = useResizeObserver({
    ref: viewerWrapperRef,
  })

  const viewerContainerSize = useMemo(() => {
    if (
      !isScreenSmallOrLarger ||
      !layoutRectangle.width ||
      !layoutRectangle.height ||
      !builderRectangle.width ||
      !builderRectangle.height
    ) {
      return [null, null] as const
    }

    return [
      layoutRectangle.width - builderRectangle.width - 20,
      isScreenSmallOrLarger ? layoutRectangle.width * 0.8 : null,
    ] as const
  }, [isScreenSmallOrLarger, layoutRectangle, builderRectangle])
  const viewerSize = useMemo(() => {
    if (!viewerWrapperRectangle.width || !viewerWrapperRectangle.height) {
      return [null, null] as const
    }

    return [
      viewerWrapperRectangle.width,
      isScreenSmallOrLarger
        ? viewerWrapperRectangle.height - 80
        : viewerWrapperRectangle.width,
    ] as const
  }, [isScreenSmallOrLarger, viewerWrapperRectangle])

  useEffect(() => {
    // Trigger window resize to read correct sizes of all elements
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 0)
  }, [])

  return (
    <section
      className={cx('container transition-opacity duration-200', {
        'opacity-0': !isDefaultStateLoaded,
      })}
    >
      <div
        ref={layoutRef}
        className="flex flex-col-reverse justify-between sm:flex-row"
      >
        <Builder
          ref={builderRef}
          frameViewerRef={frameViewerRef}
          showExtrasStep={showExtrasStep}
          showProgressBar={showProgressBar}
          layoutRectangle={layoutRectangle}
          viewerSize={viewerSize}
          isScreenSmallOrLarger={isScreenSmallOrLarger}
          onOpenStep={onOpenStep}
          onAddToCart={onAddToCart}
        />

        <div
          className="flex sm:block flex-col items-center relative w-full sm:pb-20 bg-white"
          style={{
            width: viewerContainerSize[0] ?? undefined,
            height: viewerContainerSize[1] ?? undefined,
            backgroundColor: selectedState.artwork.backgroundColor?.current,
          }}
        >
          <div ref={viewerWrapperRef} className="w-full sm:h-full">
            <FrameViewer
              ref={frameViewerRef}
              width={viewerSize[0] ?? undefined}
              height={viewerSize[1] ?? undefined}
            />
          </div>

          {selectedState.texture?.images &&
            selectedState.texture.images.length > 0 && (
              <Gallery
                images={selectedState.texture.images}
                className="sm:absolute sm:right-0 sm:bottom-0 sm:left-0 z-20"
              />
            )}
        </div>
      </div>
    </section>
  )
}

export default FrameBuilder
